import type { Translate } from '@/utils/hooks/translation/useTranslation';

// Types
import type { SocialProfileNetworkType } from '@/types/socialProfileType';

// Libs
import {
    FB_POST_TYPES,
    FBPostTypesType,
    IG_POST_TYPES,
    IG_STORY_TYPES,
    IGPostTypesType,
    IGStoryTypesType,
    LIPostTypesType,
    MENTIONS_TYPE,
    MentionsTypesType,
    PIPostTypesType,
    TIPostTypesType,
    TW_POST_TYPES,
    TWPostTypesType,
    YTPostTypesType
} from '@/lib/posts/postTypes';
import {
    FACEBOOK,
    formatSocialProfileType,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

type allType = 'all';

type PostSortInstagram = { label: string; value: IGPostTypesType | allType };
type StorySortInstagram = { label: string; value: IGStoryTypesType | allType };
type PostSortFacebook = { label: string; value: FBPostTypesType | allType };
type PostSortTwitter = { label: string; value: TWPostTypesType | allType };
type PostSortLinkedin = { label: string; value: LIPostTypesType | allType };
type PostSortPinterest = { label: string; value: PIPostTypesType | allType };
type PostSortTiktok = { label: string; value: TIPostTypesType | allType };
type PostSortYoutube = { label: string; value: YTPostTypesType | allType };
type mentionsSortTypesType = { label: string; value: MentionsTypesType | allType };

export type SortTypeType =
    | PostSortInstagram
    | StorySortInstagram
    | PostSortFacebook
    | PostSortTwitter
    | PostSortLinkedin
    | PostSortPinterest
    | PostSortTiktok
    | PostSortYoutube;

export const postsSortTypes = (t: Translate) => {
    return {
        [INSTAGRAM]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.photo'), value: IG_POST_TYPES.TYPE_PHOTO },
            { label: t('posts.video'), value: IG_POST_TYPES.TYPE_VIDEO },
            { label: t('posts.carousel'), value: IG_POST_TYPES.TYPE_CAROUSEL },
            { label: t('posts.reel'), value: IG_POST_TYPES.TYPE_REEL }
        ] as PostSortInstagram[],
        [FACEBOOK]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.status'), value: FB_POST_TYPES.TYPE_STATUS },
            { label: t('posts.photo'), value: FB_POST_TYPES.TYPE_PHOTO },
            { label: t('posts.video'), value: FB_POST_TYPES.TYPE_VIDEO },
            { label: t('posts.story'), value: FB_POST_TYPES.TYPE_STORY },
            { label: t('posts.reel'), value: FB_POST_TYPES.TYPE_REEL }
        ] as PostSortFacebook[],
        [TWITTER]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.photo'), value: TW_POST_TYPES.TYPE_PHOTO },
            { label: t('posts.video'), value: TW_POST_TYPES.TYPE_VIDEO },
            { label: t('posts.carousel'), value: TW_POST_TYPES.TYPE_CAROUSEL },
            { label: t('posts.status'), value: TW_POST_TYPES.TYPE_STATUS },
            { label: t('posts.link'), value: TW_POST_TYPES.TYPE_LINK },
            { label: t('posts.retweet_comment'), value: TW_POST_TYPES.TYPE_QUOTE },
            { label: t('posts.retweet_simple'), value: TW_POST_TYPES.TYPE_RETWEET }
        ] as PostSortTwitter[],
        [X]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.photo'), value: TW_POST_TYPES.TYPE_PHOTO },
            { label: t('posts.video'), value: TW_POST_TYPES.TYPE_VIDEO },
            { label: t('posts.carousel'), value: TW_POST_TYPES.TYPE_CAROUSEL },
            { label: t('posts.status'), value: TW_POST_TYPES.TYPE_STATUS },
            { label: t('posts.link'), value: TW_POST_TYPES.TYPE_LINK },
            { label: t('posts.retweet_comment'), value: TW_POST_TYPES.TYPE_QUOTE },
            { label: t('posts.retweet_simple'), value: TW_POST_TYPES.TYPE_RETWEET }
        ] as PostSortTwitter[],
        [LINKEDIN]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.photo'), value: 'photo' },
            { label: t('posts.video'), value: 'video' },
            { label: t('posts.carousel'), value: 'carousel' },
            { label: t('posts.status'), value: 'status' },
            { label: t('posts.link'), value: 'link' },
            { label: t('posts.document'), value: 'document' },
            { label: t('posts.poll'), value: 'poll' }
        ] as PostSortLinkedin[],
        [PINTEREST]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.image'), value: 'image' },
            { label: t('posts.multiple_images'), value: 'multiple_images' },
            { label: t('posts.multiple_mixed'), value: 'multiple_mixed' },
            { label: t('posts.multiple_videos'), value: 'multiple_videos' },
            { label: t('posts.video'), value: 'video' }
        ] as PostSortLinkedin[],
        [TIKTOK_BUSINESS]: [{ label: t('posts.video'), value: 'video' }] as PostSortTiktok[],
        [YOUTUBE]: [
            { label: t('posts.all'), value: 'all' },
            { label: t('posts.video'), value: 'video' },
            { label: t('posts.short'), value: 'short' }
        ] as PostSortYoutube[]
    };
};

export const storiesSortTypes = (t: Translate) => {
    return [
        { label: t('posts.all'), value: 'all' },
        { label: t('posts.photo'), value: IG_STORY_TYPES.TYPE_PHOTO },
        { label: t('posts.video'), value: IG_STORY_TYPES.TYPE_VIDEO }
    ] as StorySortInstagram[];
};

export const mentionsSortTypes = (t: Translate) => {
    return [
        { label: t('mentions.all'), value: 'all' },
        { label: t('mentions.caption'), value: MENTIONS_TYPE.TYPE_CAPTION },
        { label: t('mentions.comment'), value: MENTIONS_TYPE.TYPE_COMMENT },
        { label: t('mentions.tag'), value: MENTIONS_TYPE.TYPE_TAG }
    ] as mentionsSortTypesType[];
};

export const multiNetworksPostsSortTypes = (t: Translate, networks: SocialProfileNetworkType[]) => {
    const networksSortTypes = postsSortTypes(t);
    const availableTypes = [];
    networks.forEach((network) => {
        networksSortTypes[network].forEach((networkType) => {
            const index = availableTypes.findIndex((item) => item.value === networkType.value);
            if (index < 0) {
                availableTypes.push({
                    ...networkType,
                    networks: [{ social_network_type: network }]
                });
            } else {
                availableTypes[index] = {
                    ...availableTypes[index],
                    networks: [...availableTypes[index].networks, { social_network_type: network }]
                };
            }
        });
    });

    return availableTypes.map((type) => ({
        label:
            availableTypes.filter((tp) => tp.label === type.label).length > 1
                ? `${type.label} (${type.networks
                      .map((network: { social_network_type: SocialProfileNetworkType }) =>
                          formatSocialProfileType(network)
                      )
                      .join(', ')})`
                : type.label,
        value: type.value
    }));
};
